import { cn } from "lib/utils";
import type { ComponentType, ReactNode } from "react";

/** lucide-react を含む汎用アイコンコンポーネント型 (size を受け取る). */
type IconComponent = ComponentType<{
  size?: number | string;
  "aria-hidden"?: boolean | "true" | "false";
}>;

export type SegmentedControlItem<V extends string> = {
  value: V;
  label: ReactNode;
  /** lucide-react 等のアイコン. */
  icon?: IconComponent;
};

export type SegmentedControlProps<V extends string> = {
  value: V;
  items: ReadonlyArray<SegmentedControlItem<V>>;
  onChange: (next: V) => void;
  /** ラベル ( "ラベル" 等 ) を左側に出すときに使う */
  label?: ReactNode;
};

/**
 * 角丸枠の排他選択コントロール. 「全て / A / B」のような小さな切替に使う.
 * @param {SegmentedControlProps} props 値・項目配列・切替ハンドラ
 * @return {JSX.Element} セグメント
 */
export const SegmentedControl = <V extends string>({
  value,
  items,
  onChange,
  label,
}: SegmentedControlProps<V>): JSX.Element => {
  return (
    <div className="flex items-center gap-1">
      {label && <span className="text-xs text-base-content/60">{label}</span>}
      <div
        className="inline-flex rounded-md border border-slate-200 overflow-hidden"
        role="group"
      >
        {items.map(item => {
          const isActive = item.value === value;
          const Icon = item.icon;
          return (
            <button
              key={item.value}
              type="button"
              data-segment-value={item.value}
              onClick={() => onChange(item.value)}
              className={cn(
                "px-2.5 py-1 text-xs flex items-center gap-1 transition-colors",
                isActive
                  ? "bg-violet-50 text-violet-800 font-semibold"
                  : "text-base-content/70 hover:bg-slate-50",
              )}
            >
              {Icon && <Icon size={12} aria-hidden />}
              {item.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};
